import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useContext } from 'react';
import { LicenseSystemAccessSettings } from 'src/components/userManagement/UserManagement';
import AppContext, { Role } from '../AppContext';
import { GqlError } from './GqlError';
import {
    Role as BackendTypeRole,
    CreateOrEditUserInput,
    LicenseSystemAccessRole,
    ProcessedUserPayload,
} from './generated/types';
import { ModuleId } from './mappers/module';

const mutation = `mutation CreateOrEditUser($input: CreateOrEditUserInput! ) {
    createOrEditUser(input: $input) {
        id
    }
}`;

interface CreateOrEditUserProps {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    orgId: string;
    modules: Array<ModuleId>;
    groups: Array<string>;
    role: Role;
    cpGo: LicenseSystemAccessSettings;
    colorCard: LicenseSystemAccessSettings;
}

export const useCreateOrEditUser = () => {
    const context = useContext(AppContext);
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (props: CreateOrEditUserProps) => {

            const input: CreateOrEditUserInput = {
                id: props.id,
                email: props.id ? undefined : props.email,
                firstName: props.firstName,
                lastName: props.lastName,
                groups: props.groups,
                modules: props.modules,
                orgId: props.orgId,
                role: props.role as BackendTypeRole,
                licenseSystemAccess: {
                    // passing undefined means = BE does not do anything, [] means BE should remove all assignments
                    colorProofGO: !props.cpGo.assignedLicenseSystems
                        ? undefined
                        : {
                            licenseSystemIds: props.cpGo.assignedLicenseSystems,
                            role: props.cpGo.role as LicenseSystemAccessRole,
                        },
                    colorCard: !props.colorCard.assignedLicenseSystems
                        ? undefined
                        : {
                            licenseSystemIds: props.colorCard.assignedLicenseSystems,
                            role: props.colorCard.role as LicenseSystemAccessRole,
                        },
                },
            };

            //DEBUG: Do we really need this tryCatch here ??
            try {
                await (context.api.graphql({ query: mutation, variables: { input } }) as Promise<{

                    data: {
                        createOrEditUser: ProcessedUserPayload;
                    };
                    errors?: Array<GqlError>;
                }>);
                return 'success';

            } catch (e: any) {
                throw e;
            }
        },
        onSuccess: (result, variables) => {
            // on success is triggered also if the backend returns an error
            if (result === 'success') {
                void queryClient.invalidateQueries({ queryKey: ['getOrganization', variables.orgId] });
                void queryClient.removeQueries({ queryKey: ['getUserDetails', variables.id] });
                // It is not sufficient to invalidate the query, as then the cached data is loaded into the CP GO settings modal.
                // Instead, we invalidate the query and also remove the cached data.
                void queryClient.removeQueries({ queryKey: ['getLsByUserId-for-ColorProofGo', variables.id] });
                void queryClient.removeQueries({ queryKey: ['getLsByUserId-for-ColorCard', variables.id] });
            }
        },
    });
};